import { Box, Button, Chip, FormControlLabel, Switch, Typography } from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { DemoUploadCard } from "./demo/DemoUploadCard";

export const PROGRESS_CHIP = {
  NEW: { label: "New", bg: "rgba(245,199,84,0.18)", fg: "#F5C754" },
  UPDATED: { label: "Updated", bg: "rgba(245,199,84,0.18)", fg: "#F5C754" },
  COMPLETED: { label: "Completed", bg: "rgba(76,175,80,0.18)", fg: "#8BE28F" },
  SKIPPED: { label: "Skipped", bg: "rgba(255,255,255,0.08)", fg: "rgba(247,250,250,0.7)" },
};

/**
 * Overview card. The preview is a static, scaled-down snapshot of the demo
 * card (pointer events off) — interaction happens in the tutorial popup.
 */
export const TutorialCard = ({ tutorial, progress, onOpen, onStartTour, admin = null, mandatory = false }) => {
  const chip = PROGRESS_CHIP[progress] || PROGRESS_CHIP.NEW;
  const isHiddenForUsers = !!admin?.hidden;
  const isArticle = tutorial.mode === "article";

  return (
    <Box
      data-testid={`tutorial-card-${tutorial.id}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        backgroundColor: "rgba(41,56,77,0.55)",
        border: "1px solid rgba(255,255,255,0.08)",
        overflow: "hidden",
        transition: "border-color 0.2s ease, transform 0.2s ease",
        "&:hover": { borderColor: "rgba(245,199,84,0.45)" },
        ...(isHiddenForUsers ? { opacity: 0.6, borderStyle: "dashed" } : {}),
      }}
    >
      {admin && (
        <Box
          data-testid={`tutorial-admin-${tutorial.id}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            px: 1.5,
            py: 0.5,
            background: "rgba(245,199,84,0.1)",
            borderBottom: "1px solid rgba(245,199,84,0.35)",
          }}
        >
          <FormControlLabel
            sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5, color: "#F7FAFA" } }}
            control={
              <Switch
                size="small"
                checked={!admin.hidden}
                onChange={admin.onToggleHidden}
                data-testid={`tutorial-admin-visible-${tutorial.id}`}
              />
            }
            label="Visible to users"
          />
          <FormControlLabel
            sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5, color: "#F7FAFA" } }}
            control={
              <Switch
                size="small"
                checked={!admin.noPrompt && !admin.hidden}
                disabled={admin.hidden}
                onChange={admin.onToggleNoPrompt}
                data-testid={`tutorial-admin-prompt-${tutorial.id}`}
              />
            }
            label="Popup after login"
          />
          <FormControlLabel
            sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5, color: "#F7FAFA" } }}
            control={
              <Switch
                size="small"
                checked={!!admin.mandatory}
                disabled={admin.hidden}
                onChange={admin.onToggleMandatory}
                data-testid={`tutorial-admin-mandatory-${tutorial.id}`}
              />
            }
            label="Mandatory"
          />
          {isHiddenForUsers && (
            <Chip size="small" label="Hidden" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", background: "rgba(255,255,255,0.1)", color: "rgba(247,250,250,0.8)" }} />
          )}
        </Box>
      )}
      <Box
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
        data-testid={`tutorial-open-${tutorial.id}`}
        aria-label={`Open tutorial: ${tutorial.title}`}
        sx={{
          cursor: "pointer",
          height: 172,
          overflow: "hidden",
          position: "relative",
          background: "rgba(0,0,0,0.18)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {isArticle ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: "rgba(247,250,250,0.55)",
            }}
          >
            <MarkEmailReadRoundedIcon sx={{ fontSize: 44, color: "#F5C754" }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "rgba(247,250,250,0.65)" }}>
              Check Spam for “Clippn Password Reset”
            </Typography>
          </Box>
        ) : (
        <Box
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            width: 520,
            transform: "scale(0.72)",
            transformOrigin: "top left",
          }}
        >
          <DemoUploadCard state={tutorial.previewState || "uploading"} expandFiles={false} />
        </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(27,38,53,0) 55%, rgba(27,38,53,0.95) 100%)",
          }}
        />
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#F7FAFA" }}>{tutorial.title}</Typography>
          {mandatory && progress !== "COMPLETED" && (
            <Chip
              size="small"
              label="Required"
              data-testid={`tutorial-required-${tutorial.id}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", backgroundColor: "rgba(241,94,107,0.18)", color: "#FF9AA4", borderRadius: "6px" }}
            />
          )}
          <Chip
            size="small"
            icon={progress === "COMPLETED" ? <CheckCircleRoundedIcon sx={{ fontSize: 14, color: `${chip.fg} !important` }} /> : undefined}
            label={chip.label}
            data-testid={`tutorial-progress-${tutorial.id}`}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", backgroundColor: chip.bg, color: chip.fg, borderRadius: "6px" }}
          />
        </Box>
        <Typography sx={{ fontSize: 13.5, color: "rgba(247,250,250,0.75)", flex: 1 }}>{tutorial.summary}</Typography>
        <Typography sx={{ fontSize: 12, color: "rgba(247,250,250,0.5)" }}>
          {isArticle
            ? `Article · about ${tutorial.estimatedMinutes} min read`
            : `Tour · about ${tutorial.estimatedMinutes} min · ${tutorial.steps.length} steps`}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={isArticle ? <MenuBookRoundedIcon /> : <PlayArrowRoundedIcon />}
            onClick={isArticle ? onOpen : onStartTour}
            data-testid={`tutorial-start-${tutorial.id}`}
            sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none" }}
          >
            {isArticle ? (progress === "COMPLETED" ? "Read again" : "Read") : progress === "COMPLETED" ? "Replay tour" : "Start tour"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onOpen}
            data-testid={`tutorial-article-${tutorial.id}`}
            sx={{
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              color: "#F7FAFA",
              borderColor: "rgba(255,255,255,0.28)",
              "&:hover": { borderColor: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.06)" },
            }}
          >
            Open
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
